import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LocationsListParams } from "../../../admin-api/locations";
import { adminApi } from "../../../api-client";
import type { UpdateLocationImage } from "../../../schemas/location";
import type { CreateLocation, Location } from "../../../types";
import { queryKeys } from "../../query-keys";

export function useLocations(params: LocationsListParams = {}) {
	return useQuery({
		placeholderData: keepPreviousData,
		queryFn: () => adminApi.locations.list(params),
		queryKey: queryKeys.locations.list(params),
	});
}

export function useLocation(id: string) {
	return useQuery({
		enabled: !!id,
		queryFn: () => adminApi.locations.get(id),
		queryKey: queryKeys.locations.detail(id),
	});
}

export function useCreateLocation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateLocation) => adminApi.locations.create(data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useUpdateLocation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<Location> }) =>
			adminApi.locations.update(id, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useDeleteLocation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.locations.delete(id),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useDeleteLocationImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, imageId }: { id: string; imageId: string }) =>
			adminApi.locations.deleteImage(id, imageId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useVerifyLocation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.locations.verify(id),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useScrapeLocationImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.locations.scrapeOne(id),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useScrapeLocationsBulk() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (limit?: number) => adminApi.locations.scrapeBulk(limit),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useReplaceLocationImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ file, id, imageId }: { file: File; id: string; imageId: string }) =>
			adminApi.locations.replaceImage(id, imageId, file),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useImportLocationImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			attribution,
			id,
			isPrimary,
			url,
		}: {
			id: string;
			url: string;
			attribution?: string;
			isPrimary?: boolean;
		}) =>
			adminApi.locations.importImage(id, {
				attribution,
				is_primary: isPrimary,
				url,
			}),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useUploadLocationImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			file,
			isPrimary,
			caption,
		}: {
			id: string;
			file: File;
			isPrimary?: boolean;
			caption?: string;
		}) => adminApi.locations.uploadImage(id, file, { caption, isPrimary }),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}

export function useUpdateLocationImage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			data,
			id,
			imageId,
		}: {
			data: UpdateLocationImage;
			id: string;
			imageId: string;
		}) => adminApi.locations.updateImage(id, imageId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.locations.all,
			}),
	});
}
